import { Help } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";

import imageTest from "../../assets/images/general-settings-instructions.jpg";
import HelperGenericModal from "../../components/HelperGenericModal";
import PageWrapper from "../../components/PageWrapper";
import PreloadFb from "../../components/Preload";
import { useAuth } from "../../contexts/contexts";
import { getApiErrorMessage } from "../../helpers/api/getApiErrorMessage";
import { defaultToast } from "../../helpers/toast/defaultToast";
import { api } from "../../services/api";
import { getAsaasActiveAccountValidationError } from "./AcquirerSettingsList";
import { SettingsConfigPageStyle } from "./styles";
import { Acquirer } from "./types";

function AcquirerSettingsPage() {
    const [isLoading, setIsLoading] = useState(true);
    const [loadingButtonSave, setLoadingButtonSave] = useState(false);

    const [activeAcquirer, setActiveAcquirer] = useState<Acquirer | null>(null);
    const [originalAcquirer, setOriginalAcquirer] = useState<Acquirer | null>(
        null
    );
    const [acquirerOptions, setAcquirerOptions] = useState<Acquirer[]>([]);

    const [openHelperGenericModal, setOpenHelperGenericModal] = useState(false);
    const handleCloseHelperGenericModal = () =>
        setOpenHelperGenericModal(false);

    const navigate = useNavigate();
    const { hasPermissions, permissions } = useAuth();

    useEffect(() => {
        if (
            !hasPermissions("configuracoes.adquirente") &&
            permissions.length > 0
        ) {
            navigate("/Error");
        }
    }, [permissions]);

    const getAcquirerSettings = async () => {
        try {
            setIsLoading(true);
            const { data } = await api.get("/v2/acquirer/settings");

            if (data?.status) {
                const acquirers = data.response;
                setAcquirerOptions(acquirers);

                const current = acquirers.find((a: Acquirer) => a.active === 1);
                if (current) {
                    setActiveAcquirer(current);
                    setOriginalAcquirer(current);
                }
            }
        } catch (error) {
            defaultToast(
                "error",
                getApiErrorMessage(error, "Erro ao buscar dados.")
            );
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        getAcquirerSettings();
    }, []);

    const handleChange = (event: SelectChangeEvent) => {
        const selectedId = Number(event.target.value);
        const selected = acquirerOptions.find((a) => a.id === selectedId);
        if (selected) setActiveAcquirer(selected);
    };

    const handleSave = async () => {
        const validationError =
            getAsaasActiveAccountValidationError(activeAcquirer);
        if (validationError) {
            defaultToast("error", validationError);
            return;
        }

        try {
            setLoadingButtonSave(true);
            await api.put(
                `/v2/acquirer/settings/${activeAcquirer?.id}`,
                activeAcquirer
            );
            defaultToast("success", "Adquirente atualizada com sucesso");
            setOriginalAcquirer(activeAcquirer);
        } catch (error) {
            defaultToast("error", getApiErrorMessage(error, "Erro ao salvar."));
        } finally {
            setLoadingButtonSave(false);
        }
    };

    const isModified = activeAcquirer !== originalAcquirer;

    return (
        <PageWrapper>
            {isLoading ? (
                <PreloadFb />
            ) : (
                <SettingsConfigPageStyle>
                    <div className="topPageContainer">
                        <button
                            type="button"
                            onClick={() => setOpenHelperGenericModal(true)}
                            className="instructionsContainer"
                        >
                            <p>Ajuda</p>
                            <Help />
                        </button>
                    </div>

                    <h2>Configuração de Adquirente</h2>

                    <FormControl fullWidth style={{ margin: "24px 0" }}>
                        <InputLabel id="select-acquirer-label">
                            Adquirente ativa
                        </InputLabel>
                        <Select
                            labelId="select-acquirer-label"
                            value={activeAcquirer?.id.toString() || ""}
                            label="Adquirente ativa"
                            onChange={handleChange}
                        >
                            {acquirerOptions.map((option) => (
                                <MenuItem key={option.id} value={option.id}>
                                    {option.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Outlet
                        context={{
                            activeAcquirer,
                            setActiveAcquirer,
                            isModified,
                        }}
                    />

                    <LoadingButton
                        variant="contained"
                        onClick={handleSave}
                        loading={loadingButtonSave}
                    >
                        Salvar
                    </LoadingButton>

                    <HelperGenericModal
                        openHelperGenericModal={openHelperGenericModal}
                        handleCloseHelperGenericModal={
                            handleCloseHelperGenericModal
                        }
                        text="Selecione a adquirente desejada na lista e clique em 'Salvar' para ativá-la. Apenas uma adquirente pode estar ativa por vez."
                        src={imageTest}
                    />
                </SettingsConfigPageStyle>
            )}
        </PageWrapper>
    );
}

export default AcquirerSettingsPage;
